import { t } from "../i18n";
import { Check, Minus, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { DraftLine, Product } from "../types";
import { loadSticky } from "../storage";
import { draftUnitPrice, splitCounters, splitOptions, splitPricedOptions } from "../utils";
import { useBackHandler } from "../backNavigation";

type Props = {
  line: DraftLine;
  product: Product;
  showQuantity?: boolean;
  onCancel: () => void;
  onSave: (line: DraftLine | null) => void;
};

function countValue(value: string): number {
  const parsed = Number.parseInt(value.replace(/\D/g, ""), 10);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(99, parsed)) : 0;
}

export function ProductDetailsDialog({
  line,
  product,
  showQuantity = true,
  onCancel,
  onSave
}: Props) {
  const [quantity, setQuantity] = useState(line.quantity);
  const [withoutOptions, setWithoutOptions] = useState(line.withoutOptions);
  const [extras, setExtras] = useState(line.extras);
  const [counters, setCounters] = useState(line.counters);
  const [comment, setComment] = useState(line.comment);
  const [takeaway, setTakeaway] = useState(line.takeaway);
  useBackHandler(true, onCancel);
  const withoutChoices = useMemo(() => splitOptions(product.without_options), [product.without_options]);
  const extraChoices = useMemo(() => splitPricedOptions(product.extras), [product.extras]);
  const counterChoices = useMemo(() => splitCounters(product.counter_options), [product.counter_options]);
  const stickyTakeaway = loadSticky("takeaway");
  const stickyComment = loadSticky("comment");

  const takeawayField = (
    <label className="takeaway-option">
      <input
        type="checkbox"
        checked={takeaway}
        onChange={(event) => setTakeaway(event.target.checked)}
      />
      <span>{t("Πακέτο")}</span>
    </label>
  );
  const commentField = (
    <label className="comment-field">
      <span>{t("Σχόλιο")}</span>
      <textarea
        rows={2}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />
    </label>
  );

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  function updateCounter(label: string, value: number) {
    const next = { ...counters };
    if (value > 0) {
      next[label] = Math.min(99, value);
    } else {
      delete next[label];
    }
    setCounters(next);
  }

  function save() {
    if (quantity <= 0) {
      onSave(null);
      return;
    }
    onSave({
      ...line,
      quantity,
      withoutOptions,
      extras,
      counters,
      comment,
      takeaway,
      unitPrice: draftUnitPrice(product, line.variation, counters, extras)
    });
  }

  return createPortal(
    <div
      className="dialog-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <section className="details-dialog" role="dialog" aria-modal="true" aria-labelledby="details-title">
        <header className="dialog-header">
          <div>
            <h2 id="details-title">{line.name}</h2>
            <p>{t("Λεπτομέρειες προϊόντος")}</p>
          </div>
          <button type="button" className="icon-button" onClick={onCancel} aria-label={t("Κλείσιμο")}>
            <X />
          </button>
        </header>

        <div className="dialog-scroll">
          {showQuantity ? (
            <div className="quantity-editor main-quantity">
              <button type="button" className="icon-button" onClick={() => setQuantity(Math.max(0, quantity - 1))}>
                <Minus />
              </button>
              <label>
                <span>{t("Ποσότητα")}</span>
                <input
                  inputMode="numeric"
                  value={quantity}
                  onFocus={(event) => event.currentTarget.select()}
                  onChange={(event) => setQuantity(countValue(event.target.value))}
                />
              </label>
              <button type="button" className="icon-button" onClick={() => setQuantity(Math.min(99, quantity + 1))}>
                <Plus />
              </button>
            </div>
          ) : null}

          {withoutChoices.length ? (
            <fieldset className="option-group">
              <legend>{t("Χωρίς")}</legend>
              <div className="check-grid">
                {withoutChoices.map((option) => (
                  <label className="check-option" key={option}>
                    <input
                      type="checkbox"
                      checked={withoutOptions.includes(option)}
                      onChange={() => setWithoutOptions(toggle(withoutOptions, option))}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {extraChoices.length ? (
            <fieldset className="option-group">
              <legend>Extra</legend>
              <div className="check-grid">
                {extraChoices.map((option) => (
                  <label className="check-option" key={option.label}>
                    <input
                      type="checkbox"
                      checked={extras.includes(option.label)}
                      onChange={() => setExtras(toggle(extras, option.label))}
                    />
                    <span>{option.label}{option.price > 0 ? ` (+${option.price.toFixed(2)} €)` : ""}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {counterChoices.length ? (
            <fieldset className="option-group">
              <legend>{t("Μετρητές")}</legend>
              <div className="counter-list">
                {counterChoices.map((option) => {
                  const value = counters[option.label] ?? 0;
                  return (
                    <div className="counter-row" key={option.label}>
                      <button
                        className="counter-label"
                        type="button"
                        onClick={() => updateCounter(option.label, value + 1)}
                      >
                        {option.label}
                        {option.price > 0 ? <small>+{option.price.toFixed(2)} €</small> : null}
                      </button>
                      <span className="counter-controls">
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => updateCounter(option.label, value - 1)}
                        >
                          <Minus />
                        </button>
                        <input
                          inputMode="numeric"
                          value={value}
                          onFocus={(event) => event.currentTarget.select()}
                          onChange={(event) => updateCounter(option.label, countValue(event.target.value))}
                          aria-label={t("Ποσότητα {0}", option.label)}
                        />
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => updateCounter(option.label, value + 1)}
                        >
                          <Plus />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ) : null}
          {!stickyTakeaway || !stickyComment ? (
            <div className="dialog-inline-fields">
              {!stickyTakeaway ? takeawayField : null}
              {!stickyComment ? commentField : null}
            </div>
          ) : null}
        </div>

        {stickyTakeaway || stickyComment ? (
          <div className="dialog-sticky-fields">
            {stickyTakeaway ? takeawayField : null}
            {stickyComment ? commentField : null}
          </div>
        ) : null}

        <footer className="dialog-actions">
          <button type="button" className="secondary-command" onClick={onCancel}>
            <X />
            <span>{t("Ακύρωση")}</span>
          </button>
          <button type="button" className="primary-command" onClick={save}>
            <Check />
            <span>{t("Αποθήκευση")}</span>
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
}
